import get from 'lodash/get';
import map from 'lodash/map';
import property from 'lodash/property';
import reduce from 'lodash/reduce';
import { Moment } from 'moment';

import { IMoment, moment } from '@lib/moment';
import { address, fullName, isDemoUser } from '@lib/utils';
import { isCycleShortHaul } from '../../cycle/cycles';

import { DrivingPeriod } from '@lib/data/driving-period';

import { ILogInspectionReport } from './shared/common-log-details/common-log-details.component';
import {
  IAsset,
  ILogServerData,
  Log,
  ICoDriver,
  IServerLogsViolations,
  ILogVehicle,
  ILogsOdometer,
  IDispatcher,
  IAuthEvent,
} from './log';
import { ILogSuggestionData } from '@misc/log-suggestion';
import { ILogSuggestion } from '@misc/log-suggestion';
import { IServerCategorizedViolations } from '@lib/compliance-utils';

import { CYCLE_RESETS } from '@lib/cycle-resets';
import { RESTRICTION_REASON } from '@lib/data/assign-restriction-reason';

export interface ILog {
  id: number;
  date: IMoment;
  formattedDate: IMoment;
  startTime: IMoment;
  endTime: IMoment | Moment;
  timeZone: string;
  dstChange: 'start' | 'end';
  graphStep: number;
  driver: ILogDriver;
  appVersion: number;
  events: ILogEvent[];
  remarks: ILogRemark[];
  cycleResets: ILogCycleReset[];
  graphItems: Array<ILogEvent | ILogRemark | ILogCycleReset>;
  violations: ILogViolation[];
  fmErrors: string[];
  inspectionReports?: IInspectionReport[];
  inspectionReportsV2: ILogInspectionReport[];
  hoursWorked: number;
  errorsText: string;
  cycle: string;
  carrierName: string;
  carrierAddress: string;
  terminalAddress: string;
  refNo: string
  origin: string;
  destination: string;
  notes: string;
  distance: number;
  trailerNumsList: string[];
  trailerNums: string;
  assets: IAsset[];
  allAssets: IAsset[];
  vehicles: ILogVehicle[]
  tractorNums: string[];
  allVehicles: ILogVehicle[];
  odometers: ILogsOdometer;
  metricUnits: boolean;
  shortHaul: boolean;
  hasAdverseDrivingTaken: boolean;
  hasDrivingAGExceptionTaken: boolean;
  isAdverseDrivingAvailable: boolean;
  coDrivers: ICoDriver[];
  logHistory: ILogHistory[];
  coDriverFullNames: string[];
  allCoDrivers: ICoDriver[];
  hasShortHaulCycle: boolean;
  hasWaitTime: boolean;
  hasShortHaul: boolean;
  hasRestart: boolean;
  hasRestBreak: boolean;
  hasFarmSchoolBus: boolean;
  isEldEnabled: boolean;
  isEditable: boolean;
  isComplete: boolean;
  nextDayLogPresent: boolean;
  logSuggestion: any;
  driverSignature?: {
    time?: IMoment;
    url?: string;
  };
  deferralDay: number;
  deferredMinutes: number;
  totalPCMeters: number;
  reducedRestDay: number;
  reducedRestMinutes: number;
  authEvents: IAuthEvent[],
  hasAgException: boolean;
  coachingStatus?: string;
}

export interface ILogDriver {
  id: number;
  name: string;
  firstName?: string;
  nameOnLog?: string;
  companyId?: number | string;
  isActive?: boolean;
  isDemo?: boolean;
  groups?: IDriverGroup[];
  groupIds?: number[];
  manualDrivingEnabled?: boolean;
  splitInProgress?: boolean;
  driverCompanyId?: string;
  eldMode?: string;
}

export interface IServerDriverGroup {
  group: {
    id: number;
    name: string;
  }
}

export interface IDriverGroup {
  id: number;
  name: string;
}

export interface ILogCycleReset {
  id: number;
  offlineId: string;
  time: IMoment | Moment;
  type: string;
  name: string;
  hours: number;
  status: string;
  itemType?: string;
  highlighted?: boolean;
}

export interface ILogEvent {
  id: number;
  offlineId: string;
  time: Moment | IMoment;
  endTime: Moment | IMoment;
  duration: number;
  type: string;
  nextType?: string;
  location: string;
  notes: string;
  drivingPeriod: DrivingPeriod;
  itemType?: string;
  isSds: boolean;
  isManual: boolean;
  isReassignedDrivingEvent: boolean;
  isEldGenerated: boolean;
  isFirst: boolean;
  isLast: boolean;
  isStartFixed: boolean;
  eventViolation?: boolean;
  isEndFixed: boolean;
  restrictionReason: RESTRICTION_REASON;
  startSdsEldEventOfflineId: string;
  endSdsEldEventOfflineId: string;
  sourceDrivingPeriodId?: number;
  unassignDrivingPeriodId?: number;
  ignoredDriveTimeDutyStatus?: string;
  ignoredDriveTimeDispatcher?: IDispatcher;
  highlighted?: boolean;
  vehicleNum: string;
  isUpdatedByDriver: boolean;
}

export interface ILogRemark {
  id: number;
  offlineId: string;
  time: IMoment | Moment;
  location: string;
  notes: string;
  exception_log_offline_id: string;
  itemType?: string;
  highlighted?: boolean;
}

export interface ILogViolation {
  id: number;
  type: string;
  time: IMoment;
  endTime: IMoment;
  highlighted?: boolean;
}

export interface IInspectionReport {
  id: number;
  time: any;
  vehicle?: {
    id?: number;
    number?: string;
  };
  odometer: number;
  defects: any;
  hasDriverSignature: boolean;
  hasMechanicSignature: boolean;
}

export interface ILogHistory {
  id: number;
  status: string;
  reason: string;
  suggestedAt: IMoment;
  dispatchers: string[];
  approvedBy: string[];
  rejectedBy: string;
  cancelledBy: string;
  voidedBy: string;
}

export class LogViewModel {
  private log: Log;
  private data: ILogServerData;

  constructor(log: Log) {
    this.log = log;
    this.data = log.data;
  }

  public get(): ILog {
    const log = this.log;
    const data = this.data;
    const date = moment.dateInLocalTimeZone(data.date);
    const driver = data.driver;
    const events = this.mapEvents();
    const remarks = this.mapRemarks();
    const cycleResets = this.mapCycleResets();
    const graphItems = this.combineGraphItems(events, remarks, cycleResets);
    const hoursWorked = this.calculateHoursWorked(events);
    const categorizedViolations = data.categorized_violations;
    const vehicles = log.vehicles;
    const assets: IAsset[] = log.assets;
    const tractorNums: string[] = data.tractor_nums;
    const trailerNumsList: string[] = data.trailer_nums;
    const coDrivers = log.coDrivers;
    const coDriverFullNames = data.co_driver_full_names;

    return {
      id: data.id,
      date,
      formattedDate: date.format('YYYY-MM-DD'),
      startTime: log.startTime,
      endTime: log.endTime,
      timeZone: data.time_zone,
      driver: {
        id: driver.id,
        name: fullName(driver),
        firstName: driver.first_name,
        nameOnLog: fullName({ first_name: data.driver_first_name, last_name: data.driver_last_name }),
        companyId: driver.driver_company_id,
        isActive: !driver.deactivated_at,
        isDemo: isDemoUser(driver.email),
        groupIds: driver.group_ids,
        groups: driver.groups ? driver.groups.map((group: IServerDriverGroup): IDriverGroup => {
          const { id, name } = group.group;
          return {
            id,
            name,
          };
        }): null,
        manualDrivingEnabled: driver.manual_driving_enabled,
        splitInProgress: driver.split_in_progress,
      },
      dstChange: this.calcDstChange(),
      graphStep: this.calcGraphStep(),
      graphItems,
      events,
      appVersion: driver.app_version ? Number(driver.app_version.split('.')[0]) : 32,
      remarks,
      cycleResets,
      violations: this.mapViolations(),
      fmErrors: categorizedViolations.form_and_manner,
      inspectionReports: this.mapInspectionReports(), // TODO: remove this after migrating divr to v2 for all users
      inspectionReportsV2: this.mapInspectionReportsV2(),
      hoursWorked,
      errorsText: this.errorsText(),
      cycle: data.cycle,
      carrierName: data.carrier_name,
      carrierAddress: address(data, 'carrier_'),
      terminalAddress: address(data, 'terminal_'),
      refNo: data.ref_no,
      origin: data.origin,
      destination: data.destination,
      notes: data.notes,
      distance: data.total_miles != null ? Number(data.total_miles) : data.total_miles,
      trailerNumsList,
      assets,
      trailerNums: data.trailer_nums.join(', '),
      allAssets: assets.concat(data.trailer_nums.map((assetName: string) => ({ id: null, name: assetName }))),
      vehicles,
      tractorNums,
      allVehicles: vehicles.concat(tractorNums.map(num => ({ number: num, id: null }))),
      odometers: data.odometers,
      metricUnits: data.metric_units,
      shortHaul: data.short_haul,
      hasAdverseDrivingTaken: data.adverse_driving,
      isAdverseDrivingAvailable: data.exception_adverse_driving,
      hasDrivingAGExceptionTaken: data.exception_ag_driving,
      coDrivers,
      logHistory: this.mapLogHistory(),
      coDriverFullNames,
      allCoDrivers: coDrivers.concat(coDriverFullNames.map<ICoDriver>(name => ({ name, id: null }))),
      hasShortHaulCycle: isCycleShortHaul(data.cycle),
      hasWaitTime: data.exception_wait_time,
      hasShortHaul: data.exception_short_haul,
      hasRestart: data.exception_24_hour_restart,
      hasRestBreak: data.exception_8_hour_break,
      hasFarmSchoolBus: data.exception_ca_farm_school_bus,
      hasAgException: data.exception_ag_driving,
      isEldEnabled: log.isEldEnabled,
      isEditable: data.editable,
      isComplete: log.endTime.isBefore(moment()),
      nextDayLogPresent: data.next_day_log_present,
      logSuggestion: !data.log_suggestion ? null : this.mapLogSuggestion(),
      driverSignature: !data.driver_signed_at ? {} : {
        time: moment(data.driver_signed_at),
        url: data.driver_signature_url,
      },
      deferralDay: data.deferral_day,
      deferredMinutes: data.deferred_minutes,
      totalPCMeters: data.total_pc_meters,
      reducedRestDay: data.reduced_rest_day,
      reducedRestMinutes: data.reduced_rest_minutes,
      authEvents: this.log.authEvents,
      coachingStatus: data.coaching_status,
    };
  }

  private mapEvents(): ILogEvent[] {
    const log = this.log;
    const dayStartTime = log.startTime;
    const dayEndTime = log.endTime;
    const logEvents = log.events.filter(e => e.time.isBefore(dayEndTime));
    const events: ILogEvent[] = [];

    logEvents.forEach((event, index) => {
      const nextEvent = logEvents[index + 1];
      const startTime = event.time.isBefore(dayStartTime) ? dayStartTime.clone() : event.time;
      const endTime = event.endTime;
      const type = event.type;
      const isSds = Boolean(event.startSdsEldEventOfflineId);
      const isManual = event.isManual;
      const isReassignedDrivingEvent = type === 'driving' && Boolean(event.sourceDrivingPeriodId);
      const isEldGenerated = event.isEldGenerated || Boolean(event.drivingPeriod) || isSds || isReassignedDrivingEvent;
      const isFirst = index === 0;
      const eventViolation = event.type === 'driving' ? this.data.violations.some((violation: IServerLogsViolations) => {
        const violationStartTime = moment(violation.violation.start_time).mapTz(this.data.time_zone);
        const violationEndTime = moment(violation.violation.end_time).mapTz(this.data.time_zone);

        const startsWithinRange = violationStartTime.isBetween(startTime, endTime, 'seconds', '[]');
        const endsWithinRange = violationEndTime.isBetween(startTime, endTime, 'seconds', '[]');
        const spansAcrossRange = violationStartTime.isBefore(startTime) && violationEndTime.isAfter(endTime);

        return startsWithinRange || endsWithinRange || spansAcrossRange;
      }) : false;
      const isLast = index === logEvents.length - 1;
      const isStartFixed = isFirst || isEldGenerated;
      const isEndFixed = isLast || isEldGenerated;
      const restrictionReason = get(event, 'drivingPeriod.restrictionReason', null);
      if (startTime.isSameOrAfter(dayEndTime)) return;
      if (isEldGenerated && !!event.drivingPeriod) {
        if (event.type === 'off_duty') {
          event.drivingPeriod.sdsType = `pc`;
        } else if (event.type === 'on_duty') {
          event.drivingPeriod.sdsType = `ym`;
        }
      }

      events.push({
        id: event.id,
        offlineId: event.offlineId,
        time: startTime,
        endTime,
        duration: endTime.diff(startTime, 'seconds'),
        type,
        nextType: nextEvent && nextEvent.type,
        location: event.location,
        notes: event.notes,
        drivingPeriod: event.drivingPeriod,
        itemType: 'event',
        isSds,
        isManual,
        isReassignedDrivingEvent,
        isEldGenerated,
        eventViolation,
        isFirst,
        isLast,
        isStartFixed,
        isEndFixed,
        restrictionReason,
        startSdsEldEventOfflineId: event.startSdsEldEventOfflineId,
        endSdsEldEventOfflineId: event.endSdsEldEventOfflineId,
        ignoredDriveTimeDispatcher: event.ignoredDriveTimeDispatcher,
        ignoredDriveTimeDutyStatus: event.ignoredDriveTimeDutyStatus,
        vehicleNum: event.vehicleNum,
        isUpdatedByDriver: event.isUpdatedByDriver,
      });
    });

    // Second iteration to re-calculate isStartFixed/isEndFixed taking into account next/previous events
    events.forEach((event, index) => {
      const prevEvent = events[index - 1];
      const nextEvent = events[index + 1];
      event.isStartFixed = event.isStartFixed || (prevEvent && prevEvent.isEndFixed);
      event.isEndFixed = event.isEndFixed || (nextEvent && nextEvent.isStartFixed);
    });

    return events;
  }

  private mapRemarks(): ILogRemark[] {
    return this.log.remarks.map(remark => {
      return {
        id: remark.id,
        offlineId: remark.offlineId,
        time: remark.time,
        location: remark.location,
        notes: remark.notes,
        exception_log_offline_id: remark.exception_log_offline_id,
        itemType: 'remark',
      };
    });
  }

  private mapCycleResets(): ILogCycleReset[] {
    const data = this.data;
    const timeZone = data.time_zone;
    return data.cycle_resets
      .map(c => c.cycle_reset)
      .filter(r => r.status === 'claimed')
      .map(reset => {
        return {
          id: reset.id,
          offlineId: reset.offline_id,
          type: reset.type,
          name: CYCLE_RESETS[reset.type].name,
          hours: CYCLE_RESETS[reset.type].hours,
          time: moment(reset.end_time).mapTz(timeZone),
          status: reset.status,
          itemType: 'reset',
        };
      })
    ;
  }

  private mapViolations(): ILogViolation[] {
    const log = this.data;
    const timeZone = log.time_zone;
    return log.violations.map((v: IServerLogsViolations) => {
      const violation = v.violation;
      return {
        id: violation.id,
        type: violation.type,
        time: moment(violation.start_time).mapTz(timeZone),
        endTime: moment(violation.end_time).mapTz(timeZone),
      };
    });
  }

  private combineGraphItems(
    events: ILogEvent[],
    remarks: ILogRemark[],
    cycleResets: ILogCycleReset[],
  ): Array<ILogEvent | ILogRemark | ILogCycleReset> {
    return [...events, ...remarks, ...cycleResets].sort((a, b) => {
      if (a.time.isBefore(b.time)) return -1;
      if (a.time.isAfter(b.time)) return 1;
      return 0;
    });
  }

  private mapInspectionReports(): IInspectionReport[] {
    const dvirsData = this.data.inspection_reports || [];
    return dvirsData.map(item => {
      const dvir = item.inspection_report;
      return {
        id: dvir.id,
        time: moment(dvir.time),
        vehicle: !dvir.vehicle ? {} : {
          id: dvir.vehicle.id,
          number: dvir.vehicle.number,
        },
        odometer: dvir.odometer,
        defects: map(dvir.defects, property('defect.category')),
        hasDriverSignature: Boolean(dvir.driver_signed_at),
        hasMechanicSignature: Boolean(dvir.mechanic_signed_at),
      };
    });
  }

  private mapInspectionReportsV2(): ILogInspectionReport[] {
    const dvirsData: any = this.data.inspection_reports || [];
    return dvirsData.map(item => {
      const dvir = item.inspection_report;
      return {
        id: dvir.id,
        time: moment(dvir.time),
        inspectionDate: dvir.time,
        entity: !(dvir.entity || dvir.entity_num) ? {} : {
          id: dvir.entity?.id,
          number: dvir.entity ? dvir.entity.number : dvir.entity_num,
        },
        entityType: dvir.entity_type,
        odometer: dvir.odometer,
        defects: map(dvir.inspected_parts, property('category')),
        hasDriverSignature: Boolean(dvir.driver_signed_at),
        hasMechanicSignature: Boolean(dvir.mechanic_signed_at),
      };
    });
  }

  private calculateHoursWorked(events: ILogEvent[]): number {
    return reduce(events, (sum: number, e: ILogEvent) => {
      const duration = ['on_duty', 'driving'].indexOf(e.type) >= 0 ? e.duration : 0;
      return sum + duration;
    }, 0);
  }

  private errorsText(): string {
    const categorizedViolations: IServerCategorizedViolations = this.data.categorized_violations;
    const hosViolations = categorizedViolations.hos;
    const fmErrors = categorizedViolations.form_and_manner;
    const errors: string[] = [];
    if (hosViolations.length > 1) {
      errors.push($localize`${hosViolations.length} HOS violations`);
    } else if (hosViolations.length === 1) {
      errors.push($localize`1 HOS violation`);
    }
    if (fmErrors.length > 1) {
      errors.push($localize`${fmErrors.length} form & manner errors`);
    } else if (fmErrors.length === 1) {
      errors.push($localize`1 form & manner error`);
    }

    return errors.join($localize` and `);
  }

  private mapLogSuggestion(): ILogSuggestion {
    const data = this.data;
    const logSuggestion: ILogSuggestionData = data.log_suggestion;
    const approvedIds = map(logSuggestion.approved_by, property('approved_by.id'));

    return {
      id: logSuggestion.id,
      body: logSuggestion.body,
      logIds: logSuggestion.log_ids,
      driverIds: logSuggestion.driver_ids,
      drivingPeriodIds: logSuggestion.driving_period_ids,
      suggestedAt: moment(logSuggestion.suggested_at),
      drivers: map(logSuggestion.drivers, d => {
        const driver = d.driver;
        return {
          name: fullName(driver),
          firstName: driver.first_name,
          lastName: driver.last_name,
          approved: approvedIds.indexOf(driver.id) >= 0,
        };
      }),
      dispatcherNames: map(logSuggestion.dispatchers, property('dispatcher'))
        .map(fullName)
        .join(', ')
      ,
      changedAttributes: this.mapChangedAttributes(),
      changedEvents: this.mapChangedEvents(),
      changedRemarks: this.mapChangedRemarks(),
      reason: logSuggestion.reason,
    };
  }

  private mapChangedAttributes(): string[] {
    const data = this.data;
    const offlineId = data.offline_id;
    const driverId = data.driver.id;
    const logs = data.log_suggestion
    && data.log_suggestion.body
    && data.log_suggestion.body[driverId]
    && data.log_suggestion.body[driverId].logs;
    const logChanges = (logs || []).find(l => l.offline_id === offlineId);
    return logChanges ? Object.keys(logChanges) : [];
  }

  private mapChangedEvents(): string[] {
    const data = this.data;
    const driverId = data.driver.id;
    const eventChanges =
    data.log_suggestion &&
    data.log_suggestion.body &&
    data.log_suggestion.body[driverId] &&
    data.log_suggestion.body[driverId].events;
    return (eventChanges || []).map(e => e.offline_id);
  }

  private mapChangedRemarks(): string[] {
    const data = this.data;
    const driverId = data.driver.id;
    const remarkChanges =
    data.log_suggestion &&
    data.log_suggestion.body &&
    data.log_suggestion.body[driverId] &&
    data.log_suggestion.body[driverId].remarks;
    return (remarkChanges || []).map(r => r.offline_id);
  }

  private calcGraphStep(): number {
    const log = this.log;
    if (log.isEldEnabled) {
      return 1;
    } else if (log.minuteLogs) {
      return 1 * 60;
    } else if (!log.minuteLogs) {
      return 15 * 60;
    }
  }

  private calcDstChange(): 'start' | 'end' {
    const log = this.log;
    const startTime = log.startTime;
    const endTime = log.endTime;
    if (!startTime.isDST() && endTime.isDST()) {
      return 'start';
    } else if (startTime.isDST() && !endTime.isDST()) {
      return 'end';
    }
  }

  private mapLogHistory(): ILogHistory[] {
    return this.data.edit_history.map(e => {
      return {
        id: e.id,
        status: e.status,
        reason: e.reason,
        suggestedAt: moment(e.suggested_at),
        dispatchers: e.dispatchers ? e.dispatchers.map(x => fullName(x)) : null,
        approvedBy: e.approved_by.map(x => fullName(x)),
        rejectedBy: fullName(e.rejected_by),
        cancelledBy: fullName(e.cancelled_by),
        voidedBy: fullName(e.voided_by),
      };
    });
  }
}
